import { useEffect, useRef, useState } from "react";
import { FileUp, Upload, X } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Card } from "@/shared/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { useDateFormat } from "@/shared/hooks/use-date-format";
import { useLanguage } from "@/shared/hooks/use-language";
import { formatDateDisplay } from "@/shared/lib/date-utils";
import {
  CSV_ACCEPT,
  type AttachmentStatus,
  type CsvRejectionCode,
} from "@/features/portfolio/api/attachment";
import {
  IMPORT_TYPE_CONFIG,
  isTerminalParseStatus,
  type ParseRequestType,
  type ParseRequestStatus,
  type ParseResult,
} from "@/features/portfolio/api/parse-request";
import type { UseCsvImportResult } from "@/features/portfolio/hooks/use-csv-import";
import { ImportConfirmDialog } from "./ImportConfirmDialog";

/**
 * The rejected-rows table renders at most this many rows. A mostly-rejected large CSV
 * is on the order of 10^5 rows; rendering one row per rejection with one cell per raw
 * column would commit millions of DOM nodes in one go. The first rows carry the
 * pattern the user needs to fix their file; the count line says how many more there are.
 */
const MAX_REJECTED_ROWS_SHOWN = 100;

interface ImportPanelProps {
  /** The whole pipeline, owned by `useCsvImport` above the `Tabs`. Rendered only while
   *  `activeImport` is set — the heading below reads directly off that field instead of
   *  a separately-threaded `type` prop, so it can never name a different request than
   *  the one actually dispatched. */
  importState: UseCsvImportResult;
}

/**
 * Purely presentational: every piece of pipeline state comes in through `importState`,
 * owned by `useCsvImport` above the tabs. This panel holds no state of its own beyond
 * the dropzone's drag-hover styling.
 */
export const ImportPanel = ({ importState }: ImportPanelProps) => {
  const { t } = useLanguage();
  const {
    activeImport: type,
    closeImport: onClose,
    file,
    fileRejection,
    chooseFile: onChooseFile,
    referenceDate,
    setReferenceDate,
    isComplete,
    setIsComplete,
    fieldErrors,
    isSubmitting,
    submit: onSubmit,
    generalError,
    attachmentId,
    requestId,
    requestWasAdopted,
    dispatchRefused,
    restart,
    retryDispatch,
    suggestedImportType,
    switchImportType,
    awaitingTypeSwitch,
    attachmentStatus,
    scanFailed,
    scanStalled,
    scanTimedOut,
    retryScan,
    parseStatus,
    isTerminal,
    parseFailed,
    parseStalled,
    parseTimedOut,
    retryParse,
    settledReferenceDate,
    completenessApplied,
    completenessNotAppliedMessage,
    summarySuccessCount,
    summaryRejectedCount,
    result,
    rowsNeeded,
    rowsLoading,
    rowsFailed,
    retryRows,
  } = importState;

  // The one piece of state this panel owns besides the dropzone's drag styling: whether
  // the confirmation step is on screen. It is a property of this rendering, not of the
  // pipeline — nothing the hook does depends on it — so lifting it into `useCsvImport`
  // would only spread one screen's decision across two files.
  const [confirmOpen, setConfirmOpen] = useState(false);

  // `awaitingTypeSwitch` holds the panel on the pre-dispatch form even though
  // `attachmentId` is already set — a switch reuses the already-uploaded attachment (see
  // `switchImportType`'s own comment), so the file being kept must not, on its own, read
  // as a dispatch in progress. The form below still shows the file already named and, for
  // `position-parse-request`, the Position date field — nothing here re-asks the user to
  // choose a file, only to confirm the date the new request type needs.
  const inProgress = (attachmentId != null || requestId != null) && !awaitingTypeSwitch;

  // The dialog is the form's own confirm step, so it lives exactly as long as that form
  // does. The moment `submit` proceeds the panel leaves the pre-dispatch state — an
  // upload starts (`isSubmitting`), or a reused attachment arms the dispatch
  // (`inProgress`) — and the dialog goes with it. A `submit` that bailed on the date
  // check moves neither flag, so the dialog stays open with the field error showing on
  // the field that is still on screen. Deriving the close from the pipeline's own state
  // is what keeps the date check in one place: the dialog never re-implements it to
  // decide whether to close.
  useEffect(() => {
    if (inProgress || isSubmitting) setConfirmOpen(false);
  }, [inProgress, isSubmitting]);

  // Moves focus into the panel the moment it opens. The panel renders above the tab
  // strip while both entry buttons sit inside their tabs, so it is *earlier* in the DOM
  // than whichever button launched it — a Tab from that button moves forward, past the
  // panel entirely, and the thing the user just summoned is the one thing they cannot
  // reach that way. `closeImport` returns focus to that button afterwards, or to the
  // active tab's entry button once that one unmounted. On mount
  // only: a re-type on a tab switch (`retypeImport`) keeps this panel mounted, and must
  // leave focus on the tab trigger the user is moving with.
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // A restart clears the state that gates its own card synchronously, so the panel
  // re-renders without that card — and without the button just pressed — the instant the
  // handler returns. That is the same "activating a control removes it" shape
  // `closeImport` handles for the header button, with the panel itself staying mounted
  // this time, so the heading is the nearest deliberate landing spot left inside it.
  //
  // Only the synchronous resets qualify. The `scanFailed` / `parseFailed` / `rowsFailed`
  // query retries below deliberately do NOT move focus: they refetch a query that is
  // already in error, and a query keeps reporting `isError` until the new attempt
  // settles, so the failure card and the button just pressed both stay mounted for the
  // whole refetch — and stay for good when the retry fails again. Moving focus at click
  // time would decide the control's fate before it is known, and park the user on the
  // heading with the same untouched card still on screen and nothing announced. Leaving
  // focus where the user put it is correct there.
  //
  // The timed-out retry is a different shape, even though it shares this same "leave
  // focus alone at click time" wrapper: `resetElapsed()` (called by `retryScan` /
  // `retryParse` themselves, in `use-csv-import.ts`) clears `scanTimedOut` /
  // `parseTimedOut` on the very next render, before the refetch it also triggers has
  // even landed — so its card unmounts at click time, same as a synchronous reset would,
  // rather than staying mounted through the whole refetch the way a fetch-error retry
  // does. That still drops focus to `<body>`, so `scanTimedOut` / `parseTimedOut` are
  // included below alongside the three fetch-error flags — the rescue effect does not
  // care which one caused the unmount, only that a card holding focus disappeared.
  //
  // A retry that SUCCEEDS replaces its failure card, unmounting the button just pressed
  // — and a browser drops focus to <body> when the focused node is removed. The
  // click-time wrapper below cannot cover this: at click time the outcome is not known
  // yet, and moving focus then is precisely what the failing-retry case must not do. So
  // the success path is rescued after the fact instead, and only when focus was actually
  // dropped — if the user moved focus somewhere deliberate while the retry was in
  // flight, it stays where they put it.
  const failureShown = scanFailed || parseFailed || rowsFailed || scanTimedOut || parseTimedOut;
  const wasFailureShown = useRef(failureShown);
  useEffect(() => {
    const failureCleared = wasFailureShown.current && !failureShown;
    wasFailureShown.current = failureShown;
    if (!failureCleared) return;
    if (document.activeElement === document.body) headingRef.current?.focus();
  }, [failureShown]);

  const focusHeadingAfter = (action: () => void) => () => {
    action();
    headingRef.current?.focus();
  };
  const onRestart = focusHeadingAfter(restart);
  const onRetryDispatch = focusHeadingAfter(retryDispatch);
  const onScanRetry = retryScan;
  const onParseRetry = retryParse;
  const onRowsRetry = retryRows;
  // Activating "Import as <type>" clears `requestId` and sets `awaitingTypeSwitch`,
  // which synchronously unmounts the failed-request card (holding focus) in favor of the
  // pre-dispatch form — the same "activating a control removes the card that held focus"
  // shape `onRestart` / `onRetryDispatch` handle above, with no query refetch in between
  // to keep the card (and the button) mounted the way a fetch-error retry does. So this
  // belongs with `focusHeadingAfter`'s other wrapped handlers, not with the plain
  // `retryScan`/`retryParse`/`retryRows` handlers above.
  const onSwitchImportType = (requestType: ParseRequestType) =>
    focusHeadingAfter(() => switchImportType(requestType))();

  // A drop released a few pixels outside the dashed border still triggers the browser's
  // default action for the whole page — opening the dropped file and tearing down the
  // console, and whatever import is running, out from under the user. The dropzone's own
  // handlers already call `preventDefault` for drops that land on it; this only covers
  // everywhere else, for as long as the panel stays open.
  useEffect(() => {
    const suppress = (event: DragEvent) => event.preventDefault();
    window.addEventListener("dragover", suppress);
    window.addEventListener("drop", suppress);
    return () => {
      window.removeEventListener("dragover", suppress);
      window.removeEventListener("drop", suppress);
    };
  }, []);

  if (!type) return null;

  const copy = IMPORT_TYPE_CONFIG[type];

  return (
    <div className="space-y-4" data-testid="import-panel" role="region" aria-label={t(copy.title)}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 ref={headingRef} tabIndex={-1} className="font-semibold focus:outline-none">
            {t(copy.title)}
          </h3>
          <p className="text-sm text-muted-foreground">{t(copy.hint)}</p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("portfolio.import.close")}
          onClick={onClose}
          data-testid="import-close"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      {generalError && (
        <Card className="p-4 border-destructive" role="alert" data-testid="import-error">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-destructive text-sm">{generalError}</p>
            <div className="flex gap-2">
              {attachmentId && attachmentStatus === "success" && !requestId && !dispatchRefused && (
                <Button variant="outline" size="sm" onClick={onRetryDispatch} data-testid="import-error-retry">
                  {t("portfolio.retry")}
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={onRestart} data-testid="import-error-restart">
                {t("portfolio.import.startOver")}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {!inProgress && (
        <Card className="p-6 space-y-5" data-testid="import-form">
          <div className="space-y-1">
            <a
              href={copy.templateHref}
              download={copy.templateHref.split("/").pop()}
              className="text-sm font-medium text-primary underline underline-offset-2 hover:no-underline"
              data-testid="import-template-link"
            >
              {t("portfolio.import.templateLink")}
            </a>
            <p className="text-xs text-muted-foreground">{t(copy.templateHint)}</p>
          </div>

          <ImportDropzone
            file={file}
            fileRejection={fileRejection}
            onChooseFile={onChooseFile}
            isSubmitting={isSubmitting}
          />

          {/* A request type that declares completeness confirms before it starts: the
              declaration closes every position the file does not list, and the date it
              closes them as of is asked for on the same screen (see
              `ImportConfirmDialog`). Every other request type has nothing to confirm and
              starts on this click, so the button reads the config rather than naming a
              request type. */}
          <Button
            onClick={copy.declaresCompleteness ? () => setConfirmOpen(true) : onSubmit}
            disabled={!file || isSubmitting}
            data-testid="import-submit"
          >
            <FileUp className="w-4 h-4 mr-2" />
            {isSubmitting ? t("portfolio.import.uploading") : t("portfolio.import.start")}
          </Button>
        </Card>
      )}

      {copy.declaresCompleteness && (
        <ImportConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          referenceDate={referenceDate}
          onReferenceDateChange={setReferenceDate}
          referenceDateError={fieldErrors.referenceTime}
          isComplete={isComplete}
          onIsCompleteChange={setIsComplete}
          onConfirm={onSubmit}
        />
      )}

      {inProgress && (
        <ImportProgress
          attachmentStatus={attachmentStatus}
          scanFailed={scanFailed}
          scanStalled={scanStalled}
          scanTimedOut={scanTimedOut}
          onScanRetry={onScanRetry}
          parseStatus={parseStatus}
          parseFailed={parseFailed}
          parseStalled={parseStalled}
          parseTimedOut={parseTimedOut}
          onParseRetry={onParseRetry}
          hasRequest={requestId != null}
          dispatchRefused={dispatchRefused}
          onRestart={onRestart}
        />
      )}

      {isTerminal && (
        <ImportResults
          wasAdopted={requestWasAdopted}
          status={parseStatus!}
          settledReferenceDate={settledReferenceDate}
          completenessApplied={completenessApplied}
          completenessNotAppliedMessage={completenessNotAppliedMessage}
          summarySuccessCount={summarySuccessCount}
          summaryRejectedCount={summaryRejectedCount}
          result={result}
          rowsNeeded={rowsNeeded}
          rowsLoading={rowsLoading}
          rowsFailed={rowsFailed}
          onRowsRetry={onRowsRetry}
          onRestart={onRestart}
          suggestedImportType={suggestedImportType}
          onSwitchImportType={onSwitchImportType}
        />
      )}
    </div>
  );
};

interface ImportDropzoneProps {
  file: File | null;
  fileRejection: CsvRejectionCode | null;
  onChooseFile: (chosen: File | null) => void;
  isSubmitting: boolean;
}

/**
 * The picker and the drop target are the same control: a `<label>` wrapping a hidden
 * file input, so both paths land on the one `onChooseFile` call and its rejection
 * banner. Drag state uses a depth counter, not a boolean — `dragenter`/`dragleave` fire
 * for every child element the pointer crosses (the icon, the text), and a boolean flag
 * would flicker the highlight on each crossing instead of holding it for the whole
 * hover.
 */
const ImportDropzone = ({ file, fileRejection, onChooseFile, isSubmitting }: ImportDropzoneProps) => {
  const { t } = useLanguage();
  const [dragDepth, setDragDepth] = useState(0);
  const isDragging = dragDepth > 0;

  // The panel used to remount fresh on every tab switch, which reset `dragDepth` for
  // free. Now it survives, so a drag that never reaches a matching `dragleave` — pulled
  // out of the window, or interrupted by the window losing focus — leaves the counter
  // stuck above zero and the highlight showing until the panel is closed. `dragend` and
  // a lost window focus are the two signals that mean the drag is over regardless of
  // where the pointer went, so reset on both; the enter/leave-over-children counting
  // above is untouched, since that's the case it exists for.
  useEffect(() => {
    const resetDragDepth = () => setDragDepth(0);
    window.addEventListener("dragend", resetDragDepth);
    window.addEventListener("blur", resetDragDepth);

    return () => {
      window.removeEventListener("dragend", resetDragDepth);
      window.removeEventListener("blur", resetDragDepth);
    };
  }, []);

  return (
    <div className="relative">
      {/* `sr-only`, not `hidden`: a display-none input leaves the tab order, and
          the picker must stay reachable and triggerable by keyboard. The label
          is what renders; the focus ring lands on it through `peer`. */}
      <input
        id="import-file"
        type="file"
        className="peer sr-only"
        accept={CSV_ACCEPT}
        // Disabled while an upload is in flight: a file swapped mid-upload would
        // race the running continuation for the pipeline — the same hijack the
        // dispatch path guards against — so the swap is made impossible instead.
        disabled={isSubmitting}
        onChange={(event) => {
          const chosen = event.target.files?.[0] ?? null;
          // Reset so re-choosing the same (fixed-on-disk) file fires a fresh
          // `change` instead of silently keeping the stale rejection.
          event.target.value = "";
          onChooseFile(chosen);
        }}
      />
      <label
        htmlFor="import-file"
        data-testid="import-dropzone"
        className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-disabled:cursor-not-allowed peer-disabled:opacity-50 peer-disabled:hover:border-muted-foreground/30 ${
          isDragging ? "border-ring bg-accent/10" : "border-muted-foreground/30 hover:border-muted-foreground/60"
        }`}
        onDragOver={(event) => {
          // Required or the browser navigates to the dropped file instead of
          // firing `drop` on this element.
          event.preventDefault();
          event.dataTransfer.dropEffect = isSubmitting ? "none" : "copy";
        }}
        onDragEnter={(event) => {
          event.preventDefault();
          setDragDepth((depth) => depth + 1);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setDragDepth((depth) => Math.max(0, depth - 1));
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragDepth(0);
          if (isSubmitting) return;
          // A drag carrying no file at all — dragged text, a link, an image from
          // another page — must leave the current choice alone. Passing the absent
          // file straight through would read as `chooseFile(null)` and silently
          // clear a file the user had already chosen.
          if (event.dataTransfer.files.length === 0) return;
          // A multi-file drop takes the first file, matching the single-file picker.
          onChooseFile(event.dataTransfer.files[0]);
        }}
      >
        <Upload className="w-6 h-6 text-muted-foreground" />
        {file ? (
          // `min-w-0` lets this flex item shrink below the filename's intrinsic
          // width instead of forcing the dropzone (and the page under it) wider;
          // `truncate` on the nested span then clips it with an ellipsis instead
          // of overflowing. Right padding clears the clear-file button below,
          // which now sits on top of this label rather than inside it.
          <span className="text-sm font-medium min-w-0 max-w-full px-6">
            <span className="block truncate" title={file.name}>{file.name}</span>
          </span>
        ) : (
          <span className="text-sm font-medium">
            {isDragging ? t("portfolio.import.dropHere") : t("portfolio.import.choose")}
          </span>
        )}
        <span className="text-xs text-muted-foreground">{t("portfolio.import.csvOnly")}</span>
      </label>
      {/* A sibling of the `<label>`, not nested inside it: a `<button>` inside a
          `<label for>` is a spec violation, and the label's subtree also computes
          `#import-file`'s accessible name — nesting it here made the input announce
          as the filename plus this button's own label plus the size hint.
          Positioned on top of the label instead, so it still reads as attached to
          the dropzone without being part of its clickable-anywhere surface. */}
      {file && (
        <button
          type="button"
          aria-label={t("portfolio.import.clearFile")}
          disabled={isSubmitting}
          data-testid="import-file-clear"
          className="absolute top-2 right-2 rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
          onClick={() => onChooseFile(null)}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
      {fileRejection && (
        <p className="mt-2 text-sm text-destructive" role="alert" data-testid="import-file-rejected">
          {t(`portfolio.import.reject.${fileRejection}`)}
        </p>
      )}
    </div>
  );
};

interface ImportProgressProps {
  attachmentStatus?: AttachmentStatus;
  scanFailed: boolean;
  scanStalled: boolean;
  /** True once the scan poll gave up after `POLL_TIMEOUT_MS` with no terminal status and
   *  no error — every read up to that point succeeded, so this renders its own
   *  non-destructive card, never `scanFailed`'s. */
  scanTimedOut: boolean;
  onScanRetry: () => void;
  parseStatus?: ParseRequestStatus;
  parseFailed: boolean;
  parseStalled: boolean;
  /** True once the parse poll gave up after `POLL_TIMEOUT_MS` with no terminal status and
   *  no error — every read up to that point succeeded, so this renders its own
   *  non-destructive card, never `parseFailed`'s. */
  parseTimedOut: boolean;
  onParseRetry: () => void;
  hasRequest: boolean;
  /** True once the dispatch has refused a cross-portfolio adoption for this attachment —
   * a deterministic dead end with no request pending, so no progress card belongs here. */
  dispatchRefused: boolean;
  onRestart: () => void;
}

/**
 * The pipeline's own state, reported as what it is at each step.
 *
 * The three refusals below mean three different things to the user and each names its
 * own move: a quarantined file is never retried, a file that never became servable is
 * re-uploaded, and a file no schema could be derived from needs its columns fixed.
 * Collapsing them into one "upload failed" would tell the user to retry a file the
 * platform will refuse every time.
 */
const ImportProgress = ({
  attachmentStatus,
  scanFailed,
  scanStalled,
  scanTimedOut,
  onScanRetry,
  parseStatus,
  parseFailed,
  parseStalled,
  parseTimedOut,
  onParseRetry,
  hasRequest,
  dispatchRefused,
  onRestart,
}: ImportProgressProps) => {
  const { t } = useLanguage();

  if (scanFailed) {
    return (
      <Card className="p-6 border-destructive" role="alert" data-testid="import-scan-failed">
        <p className="text-destructive mb-4">{t("portfolio.import.scanCheckFailed")}</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onScanRetry} data-testid="import-scan-retry">
            {t("portfolio.retry")}
          </Button>
          <Button variant="ghost" onClick={onRestart} data-testid="import-scan-restart">
            {t("portfolio.import.startOver")}
          </Button>
        </div>
      </Card>
    );
  }

  if (attachmentStatus === "rejected") {
    return (
      <Card className="p-6 border-destructive" role="alert" data-testid="import-quarantined">
        <p className="text-destructive mb-4">{t("portfolio.import.quarantined")}</p>
        <Button variant="outline" onClick={onRestart} data-testid="import-quarantined-restart">
          {t("portfolio.import.startOver")}
        </Button>
      </Card>
    );
  }

  if (attachmentStatus === "failed") {
    return (
      <Card className="p-6 border-destructive" role="alert" data-testid="import-not-stored">
        <p className="text-destructive mb-4">{t("portfolio.import.notStored")}</p>
        <Button variant="outline" onClick={onRestart} data-testid="import-not-stored-restart">
          {t("portfolio.import.uploadAgain")}
        </Button>
      </Card>
    );
  }

  if (parseFailed) {
    return (
      <Card className="p-6 border-destructive" role="alert" data-testid="import-parse-check-failed">
        <p className="text-destructive mb-4">{t("portfolio.import.parseCheckFailed")}</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onParseRetry} data-testid="import-parse-retry">
            {t("portfolio.retry")}
          </Button>
          <Button variant="ghost" onClick={onRestart} data-testid="import-parse-restart">
            {t("portfolio.import.startOver")}
          </Button>
        </div>
      </Card>
    );
  }

  if (isTerminalParseStatus(parseStatus)) return null;

  // The refusal already has its own error banner with only "Start over" — no request is
  // pending, and none of the branches above apply, so there is no ongoing step left for
  // a progress card to describe.
  if (dispatchRefused) return null;

  // A timed-out poll is not a confirmed failure — every read up to this point succeeded,
  // it simply never turned terminal within `POLL_TIMEOUT_MS` — so this renders its own
  // non-destructive `role="status"` card rather than falling into `scanFailed`'s /
  // `parseFailed`'s destructive `role="alert"` one above. Checked after every real
  // failure branch (a genuine error always wins) and before the stalled/progress
  // fallback below (every timed-out moment is also a stalled one, so the fallback would
  // otherwise render its own, less specific notice for the same condition).
  if (scanTimedOut) {
    return (
      <Card className="p-6" role="status" data-testid="import-scan-timed-out">
        <p className="mb-4">{t("portfolio.import.scanTimedOut")}</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onScanRetry} data-testid="import-scan-timed-out-retry">
            {t("portfolio.retry")}
          </Button>
          <Button variant="ghost" onClick={onRestart} data-testid="import-scan-timed-out-restart">
            {t("portfolio.import.startOver")}
          </Button>
        </div>
      </Card>
    );
  }

  if (parseTimedOut) {
    return (
      <Card className="p-6" role="status" data-testid="import-parse-timed-out">
        <p className="mb-4">{t("portfolio.import.parseTimedOut")}</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onParseRetry} data-testid="import-parse-timed-out-retry">
            {t("portfolio.retry")}
          </Button>
          <Button variant="ghost" onClick={onRestart} data-testid="import-parse-timed-out-restart">
            {t("portfolio.import.startOver")}
          </Button>
        </div>
      </Card>
    );
  }

  const stage = !hasRequest
    ? attachmentStatus === "success"
      ? "starting"
      : "scanning"
    : "parsing";

  // "starting" is the single dispatch POST between a cleared scan and the parse request
  // existing — it is not polled, so it can never stall; the ternary already reads false
  // for it since neither branch matches. A hung dispatch surfaces only through the
  // existing `generalError` path, with no elapsed-time signal of its own — the shared API
  // client has no request timeout, and that gap is tracked as its own follow-up, not as a
  // stall mechanism here.
  const stalled = stage === "scanning" ? scanStalled : stage === "parsing" ? parseStalled : false;

  return (
    // `role="status"`, without `aria-busy`: `aria-busy="true"` tells assistive tech to
    // hold announcements for the region, which works against the announcement this role
    // exists to make. The shimmer bar below is decorative and needs no busy state. The
    // card stays exactly this one, even once `stalled` — a long-running import is not a
    // failure, so it must never adopt the destructive styling the error cards use.
    <Card className="p-6" data-testid={`import-${stage}`} role="status">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* `max-w-sm` bounds the hint's line length to the same column width for both
            copy variants. `stalledHint` runs 2-3x longer than `stageHint`, and without
            a shared cap the button shares a row with the short copy but not the long
            one — an unprompted stall mid-read would then also shove the button onto
            its own line at exactly the same moment. Capping both to one column makes
            the wrap point (and so the button's position) depend only on viewport
            width, never on which hint is showing. */}
        <div className="max-w-sm">
          <p className="font-medium">{t(`portfolio.import.stage.${stage}`)}</p>
          <p
            className="text-sm text-muted-foreground"
            data-testid={stalled ? "import-stalled-notice" : undefined}
          >
            {t(stalled ? "portfolio.import.stalledHint" : "portfolio.import.stageHint")}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onRestart} data-testid="import-cancel">
          {t("portfolio.import.startOver")}
        </Button>
      </div>
      <div className="skeleton-shimmer h-2 w-full rounded bg-muted mt-4" />
    </Card>
  );
};

interface ImportResultsProps {
  /** True when these results were adopted from an earlier import of the same file. */
  wasAdopted: boolean;
  status: ParseRequestStatus;
  /** The date the settled request was actually dispatched with, for the completeness
   *  line below — the instant the positions the file did not list were closed as of.
   *  Read off the request itself (`useCsvImport`'s `settledReferenceDate`), never off
   *  the live form field: on an adopted request, or once the panel has moved on to a
   *  later attempt, the field no longer holds the date this settled request closed
   *  positions as of. `null` when the settled request carries none to show. */
  settledReferenceDate: string | null;
  /** True only on a `success` request the parser reports the declaration was made on —
   *  see `completenessApplied` in `use-csv-import.ts`. */
  completenessApplied: boolean;
  /** The parser's own sentence for a declaration that was asked for and not applied, or
   *  null. Read only on the settled card below: a `failed` request never reached dispatch,
   *  and its own card already says no row was read at all, so repeating that nothing was
   *  closed would add a second sentence for a thing that provably did not happen. */
  completenessNotAppliedMessage: string | null;
  /** Counts from the poll's summaries; null when no summaries were embedded. */
  summarySuccessCount: number | null;
  summaryRejectedCount: number | null;
  result: ParseResult | null;
  rowsNeeded: boolean;
  rowsLoading: boolean;
  rowsFailed: boolean;
  onRowsRetry: () => void;
  onRestart: () => void;
  /** Set only on a `failed` request whose reasons name the OTHER request type — see
   *  `suggestedImportType` in `use-csv-import.ts`. Read only when `status === "failed"`. */
  suggestedImportType: ParseRequestType | null;
  onSwitchImportType: (requestType: ParseRequestType) => void;
}

const ImportResults = ({
  wasAdopted,
  status,
  settledReferenceDate,
  completenessApplied,
  completenessNotAppliedMessage,
  summarySuccessCount,
  summaryRejectedCount,
  result,
  rowsNeeded,
  rowsLoading,
  rowsFailed,
  onRowsRetry,
  onRestart,
  suggestedImportType,
  onSwitchImportType,
}: ImportResultsProps) => {
  const { t } = useLanguage();
  const { dateFormat } = useDateFormat();

  // `failed` never reached dispatch, so it has no result files to read — reporting it
  // through the rows view would show two empty tables for a request that never parsed.
  if (status === "failed") {
    // The file's columns read as the OTHER request type's shape — a positions list
    // uploaded through "Import trades" is the case this exists for. Naming the column
    // headers and offering a fresh upload (the generic card below) would send the user
    // back to fix a file that was never broken; this card names what the file actually
    // is and offers to continue as that request type instead, reusing the attachment
    // already uploaded and scanned.
    if (suggestedImportType) {
      return (
        <Card className="p-6 border-destructive" role="alert" data-testid="import-shape-mismatch">
          <p className="text-destructive mb-4">
            {t(`portfolio.import.shapeMismatch.${suggestedImportType}`)}
          </p>
          {/* `flex-wrap`: this row carries the panel's longest button pair (the `pt`
              strings), marginal at the 375px mobile width every other button row in
              this file already tolerates without it. */}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => onSwitchImportType(suggestedImportType)}
              data-testid="import-switch-type"
            >
              {t(`portfolio.import.switchTo.${suggestedImportType}`)}
            </Button>
            <Button variant="outline" onClick={onRestart} data-testid="import-shape-mismatch-restart">
              {t("portfolio.import.startOver")}
            </Button>
          </div>
        </Card>
      );
    }
    return (
      <Card className="p-6 border-destructive" role="alert" data-testid="import-no-rows-mapped">
        <p className="text-destructive mb-4">{t("portfolio.import.noRowsMapped")}</p>
        <Button variant="outline" onClick={onRestart} data-testid="import-no-rows-restart">
          {t("portfolio.import.uploadAgain")}
        </Button>
      </Card>
    );
  }

  // The summaries are the primary source of the headline counts; the row payload backs
  // them up only when a deployment embedded none. Either way, a count is rendered only
  // once something real carries it.
  const successCount = summarySuccessCount ?? (result ? result.success_rows.length : null);
  const rejectedCount = summaryRejectedCount ?? (result ? result.rejected_rows.length : null);

  if (successCount == null || rejectedCount == null) {
    if (rowsLoading) {
      return (
        <Card className="p-6" data-testid="import-results-loading" aria-busy="true">
          <div className="skeleton-shimmer h-4 w-full rounded bg-muted mb-3" />
          <div className="skeleton-shimmer h-4 w-5/6 rounded bg-muted" />
        </Card>
      );
    }

    // A read that failed knows nothing about how many rows landed. Saying so — instead
    // of rendering zeroes — is the difference between "we could not load this" and a
    // claim that the import produced nothing.
    return (
      <Card className="p-6 border-destructive" role="alert" data-testid="import-results-failed">
        <p className="text-destructive mb-4">{t("portfolio.import.resultsFailed")}</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onRowsRetry} data-testid="import-results-retry">
            {t("portfolio.retry")}
          </Button>
          <Button variant="ghost" onClick={onRestart} data-testid="import-results-restart">
            {t("portfolio.import.startOver")}
          </Button>
        </div>
      </Card>
    );
  }

  const rejectedRows = result?.rejected_rows ?? null;
  const shownRows = rejectedRows?.slice(0, MAX_REJECTED_ROWS_SHOWN) ?? [];
  const rawColumns = Array.from(
    new Set(shownRows.flatMap((row) => Object.keys(row.raw ?? {}))),
  );

  return (
    <div className="space-y-4" data-testid="import-results">
      {/* `role="status"` announces the settle; the sibling failure cards already
          announce themselves through `role="alert"`. */}
      <Card className="p-6" role="status">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-8">
            <div>
              <p className="text-sm text-muted-foreground">{t("portfolio.import.created")}</p>
              <p className="text-2xl font-bold" data-testid="import-success-count">
                {successCount}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t("portfolio.import.rejected")}</p>
              <p className="text-2xl font-bold" data-testid="import-rejected-count">
                {rejectedCount}
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={onRestart} data-testid="import-restart">
            {t("portfolio.import.importAnother")}
          </Button>
        </div>
        <p className="mt-4 text-sm text-muted-foreground" data-testid="import-outcome">
          {t(`portfolio.import.outcome.${status}`)}
        </p>
        {/* Read off the settled request's own flag and its own reference_time, never off
            the checkbox or date field that asked for them: the parser flips the flag to
            false on any terminal event that declared nothing, so this line appears only
            where the declaration is confirmed made, and it must still name the date THIS
            request settled with even once the form has moved on to a later attempt (an
            adopted request is the case that made this matter). */}
        {completenessApplied && (
          <p className="mt-2 text-sm text-muted-foreground" data-testid="import-complete-applied">
            {t("portfolio.import.completeApplied")
              .replace("{date}", formatDateDisplay(settledReferenceDate, dateFormat))}
          </p>
        )}
        {/* The other half of the same fact, in the parser's own words: the declaration was
            asked for and not applied. Rendered as it comes — the console has no fix to
            offer alongside it, so a hand-written copy would only be a second version of a
            sentence the parser already maintains in both languages. */}
        {completenessNotAppliedMessage && (
          <p className="mt-2 text-sm text-muted-foreground" data-testid="import-complete-not-applied">
            {completenessNotAppliedMessage}
          </p>
        )}
        {/* A `success` request that still carries the reason had no rejected row to fix:
            the parser withdrew the declaration for a reason outside the file, and the
            client key adopts this same request on every re-import of these bytes, so the
            one thing the user would try next is the one thing that cannot work. Said only
            here — on a `partial` the parser's own sentence already points at the rows. */}
        {completenessNotAppliedMessage && status === "success" && (
          <p className="mt-2 text-sm text-muted-foreground" data-testid="import-complete-not-retryable">
            {t("portfolio.import.completeNotRetryable")}
          </p>
        )}
        {/* "Recorded" is the parser's word, not the ledger's: each recorded row still
            books asynchronously, and only booking opens a position. Said here, under the
            outcome that could otherwise read as the last step — and only for outcomes
            that recorded something, since `rejected` has nothing left to book. */}
        {(status === "success" || status === "partial") && (
          <p className="mt-2 text-sm text-muted-foreground" data-testid="import-booking-continues">
            {t("portfolio.import.bookingContinues")}
          </p>
        )}
        {/* An adopted request settled on an *earlier* import of this same file. Without
            this line the screen would present those recorded results as work this
            submission just did. */}
        {wasAdopted && (
          <p className="mt-2 text-sm text-muted-foreground" data-testid="import-replay-notice">
            {t("portfolio.import.replayNotice")}
          </p>
        )}
      </Card>

      {rowsNeeded && rowsLoading && (
        <Card className="p-6" data-testid="import-rejected-rows-loading" aria-busy="true">
          <div className="skeleton-shimmer h-4 w-full rounded bg-muted mb-3" />
          <div className="skeleton-shimmer h-4 w-5/6 rounded bg-muted" />
        </Card>
      )}

      {/* The counts above already told the truth; this failure is only about listing
          the rows, so it reports exactly that and keeps its own retry. */}
      {rowsNeeded && !rowsLoading && rowsFailed && (
        <Card className="p-6 border-destructive" role="alert" data-testid="import-rejected-rows-failed">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-destructive text-sm">{t("portfolio.import.rowsFailed")}</p>
            <Button variant="outline" size="sm" onClick={onRowsRetry} data-testid="import-rejected-rows-retry">
              {t("portfolio.retry")}
            </Button>
          </div>
        </Card>
      )}

      {rejectedRows != null && rejectedRows.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="p-4 border-b">
            <h4 className="font-semibold">{t("portfolio.import.rejectedTitle")}</h4>
            <p className="text-sm text-muted-foreground">{t("portfolio.import.rejectedHint")}</p>
          </div>
          <div className="overflow-x-auto">
            <Table data-testid="import-rejected-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("portfolio.import.col.row")}</TableHead>
                  {rawColumns.map((column) => (
                    <TableHead key={column}>{column}</TableHead>
                  ))}
                  <TableHead>{t("portfolio.import.col.reason")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shownRows.map((row) => (
                  <TableRow key={row.row_number} data-testid="import-rejected-row">
                    <TableCell data-testid="import-rejected-row-number">{row.row_number}</TableCell>
                    {rawColumns.map((column) => (
                      <TableCell key={column} className="whitespace-nowrap">
                        {String(row.raw?.[column] ?? "")}
                      </TableCell>
                    ))}
                    <TableCell className="text-destructive" data-testid="import-rejected-reason">
                      {(row.reasons ?? []).map((reason) => reason.message).join("; ")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {rejectedRows.length > shownRows.length && (
            <p className="p-4 text-sm text-muted-foreground border-t" data-testid="import-rejected-truncated">
              {t("portfolio.import.rejectedTruncated")
                .replace("{shown}", String(shownRows.length))
                .replace("{total}", String(rejectedRows.length))}
            </p>
          )}
        </Card>
      )}
    </div>
  );
};
